import { Text } from "@radix-ui/themes";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

import { type TourStep } from "./tourSteps";

type Rect = { top: number; left: number; width: number; height: number };

const SPOTLIGHT_PADDING = 6; // breathing room around the highlighted element
const CARD_WIDTH = 268;
const CARD_FALLBACK_HEIGHT = 180; // used until the card has been measured
const VIEWPORT_MARGIN = 12;
const ARROW_SIZE = 12; // side of the rotated square forming the pointer
const ARROW_EDGE_INSET = 14; // keeps the arrow clear of the rounded corners
const ARROW_CLEARANCE = 8; // clear space between the arrow tip and the spotlight
// Derived: the gap is measured from the target rect, but the spotlight and the
// arrow both eat into it.
const CARD_GAP = SPOTLIGHT_PADDING + ARROW_SIZE / 2 + ARROW_CLEARANCE;
const MAX_MISSING_FRAMES = 90;

export type SpotlightOverlayProps = {
  steps: TourStep[];
  /** Reached the end of the tour. */
  onFinish: () => void;
  /** Dismissed early (Skip / Escape). */
  onSkip: () => void;
};

const sameRect = (a: Rect | null, b: Rect) =>
  a !== null &&
  Math.abs(a.top - b.top) < 0.5 &&
  Math.abs(a.left - b.left) < 0.5 &&
  Math.abs(a.width - b.width) < 0.5 &&
  Math.abs(a.height - b.height) < 0.5;

/**
 * Renders the dimmed backdrop with a "hole" over the current step's target
 * (a giant box-shadow around the target rect) plus a coachmark card pointing at
 * it. If a target never appears it advances rather than trapping the user
 * behind a blank scrim.
 */
export default function SpotlightOverlay({
  steps,
  onFinish,
  onSkip,
}: SpotlightOverlayProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [cardHeight, setCardHeight] = useState(CARD_FALLBACK_HEIGHT);
  // The caller can shorten `steps` under an already-advanced index.
  const safeIndex = Math.min(stepIndex, steps.length - 1);
  const step = steps[safeIndex];
  const isLast = safeIndex === steps.length - 1;
  const cardRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // In a ref so the loop below depends only on the step — the caller re-creates
  // this each render, and restarting the loop blinks the spotlight off.
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  // Re-read every frame, not once: the sidebar shifts as the dashboard's
  // queries resolve, which used to strand the hole until the next scroll.
  useLayoutEffect(() => {
    let raf = 0;
    let missingFrames = 0;
    let cancelled = false;
    setRect(null);

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(step.targetSelector);

      if (!el) {
        missingFrames += 1;
        if (missingFrames > MAX_MISSING_FRAMES) {
          if (isLast) onFinishRef.current();
          else setStepIndex((i) => i + 1);
          return;
        }
        setRect(null);
      } else {
        missingFrames = 0;
        const r = el.getBoundingClientRect();
        const next = {
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
        };
        setRect((prev) => (sameRect(prev, next) ? prev : next));
      }

      raf = requestAnimationFrame(tick);
    };

    tick();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [step.targetSelector, isLast]);

  // Measured, not guessed — copy length varies per step.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const update = () => setCardHeight(card.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  // Escape dismisses the tour.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSkip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSkip]);

  // Move focus into the dialog on open so screen readers treat the page behind
  // it as inert, then hand focus back to wherever it was when the tour closes.
  useEffect(() => {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    cardRef.current?.focus();
    return () => restoreFocusRef.current?.focus?.();
  }, []);

  // Trap focus inside the coachmark: Tab / Shift+Tab cycle within the card so a
  // keyboard user can't reach the inert app behind the scrim. Capture phase so
  // it runs before the browser's own tab handling.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const card = cardRef.current;
      if (!card) return;
      const focusables = Array.from(
        card.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) {
        e.preventDefault();
        card.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeInside = card.contains(document.activeElement);
      if (e.shiftKey) {
        if (!activeInside || document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!activeInside || document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  const handleNext = () => (isLast ? onFinish() : setStepIndex(safeIndex + 1));
  const handleBack = () => setStepIndex(Math.max(0, safeIndex - 1));

  const placement = step.placement ?? "right";
  const isVertical = placement === "top" || placement === "bottom";
  let cardStyle: CSSProperties = { display: "none" };
  // Distance along the card edge the arrow sits on, from its top or left.
  let arrowOffset = 0;

  if (rect) {
    if (isVertical) {
      // Card sits above or below the target, centered on it and clamped to the
      // viewport. "top" anchors by `bottom` so its own height needn't be known.
      const width = Math.min(
        CARD_WIDTH,
        window.innerWidth - VIEWPORT_MARGIN * 2,
      );
      const centerX = rect.left + rect.width / 2;
      const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(
          centerX - width / 2,
          window.innerWidth - width - VIEWPORT_MARGIN,
        ),
      );
      cardStyle = {
        position: "fixed",
        ...(placement === "top"
          ? { bottom: window.innerHeight - rect.top + CARD_GAP }
          : { top: rect.top + rect.height + CARD_GAP }),
        left,
        width,
        zIndex: 10001,
      };
      arrowOffset = Math.max(
        ARROW_EDGE_INSET,
        Math.min(centerX - left, width - ARROW_EDGE_INSET),
      );
    } else {
      const left = Math.min(
        rect.left + rect.width + CARD_GAP,
        window.innerWidth - CARD_WIDTH - VIEWPORT_MARGIN,
      );
      // Centered on the target so the arrow lands on it, not merely near it.
      const centerY = rect.top + rect.height / 2;
      const top = Math.max(
        VIEWPORT_MARGIN,
        Math.min(
          centerY - cardHeight / 2,
          window.innerHeight - cardHeight - VIEWPORT_MARGIN,
        ),
      );
      cardStyle = {
        position: "fixed",
        top,
        left,
        width: CARD_WIDTH,
        zIndex: 10001,
      };
      arrowOffset = Math.max(
        ARROW_EDGE_INSET,
        Math.min(centerY - top, cardHeight - ARROW_EDGE_INSET),
      );
    }
  }

  return createPortal(
    <div>
      {/* Click catcher: freezes the app behind the tour. */}
      <div className="fixed inset-0 z-[9999]" />

      {/* Spotlight hole via a giant box-shadow around the target. */}
      {rect && (
        <div
          className={cn(
            "pointer-events-none fixed z-[10000] transition-all duration-150 ease-out",
            step.round ? "rounded-full" : "rounded-[8px]",
          )}
          style={{
            top: rect.top - SPOTLIGHT_PADDING,
            left: rect.left - SPOTLIGHT_PADDING,
            width: rect.width + SPOTLIGHT_PADDING * 2,
            height: rect.height + SPOTLIGHT_PADDING * 2,
            boxShadow: "0 0 0 9999px rgba(5,12,43,0.55)",
          }}
        />
      )}

      {/* Coachmark card. */}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="audiolab-tour-title"
        aria-describedby="audiolab-tour-body"
        tabIndex={-1}
        style={cardStyle}
        className="relative rounded-[12px] bg-white p-[16px] shadow-[0_12px_40px_rgba(5,12,43,0.28)] outline-none"
        data-testid="audiolab-tour-card"
      >
        {/* Rotated square, half outside the card — reads as a triangle. */}
        <div
          aria-hidden
          className="absolute rotate-45 bg-white"
          style={{
            width: ARROW_SIZE,
            height: ARROW_SIZE,
            ...(isVertical
              ? {
                  left: arrowOffset - ARROW_SIZE / 2,
                  ...(placement === "top"
                    ? { bottom: -ARROW_SIZE / 2 }
                    : { top: -ARROW_SIZE / 2 }),
                }
              : { left: -ARROW_SIZE / 2, top: arrowOffset - ARROW_SIZE / 2 }),
          }}
        />

        <div className="mb-[6px] flex items-center justify-between">
          <Text className="text-[12px] font-semibold uppercase tracking-wide text-brand-primary">
            Step {safeIndex + 1} of {steps.length}
          </Text>
          {!isLast && (
            <button
              type="button"
              onClick={onSkip}
              data-testid="audiolab-tour-skip"
              className="text-[12px] font-semibold text-text-secondary hover:text-text-primary"
            >
              Skip
            </button>
          )}
        </div>

        <div aria-live="polite">
          <Text
            id="audiolab-tour-title"
            className="mb-[4px] block text-[16px] font-semibold text-text-primary"
          >
            {step.title}
          </Text>
          <Text
            id="audiolab-tour-body"
            className="mb-[16px] block text-[13px] leading-[140%] text-text-secondary"
          >
            {step.body}
          </Text>
        </div>

        <div className="flex items-center justify-end gap-[8px]">
          {safeIndex > 0 && (
            <button
              type="button"
              onClick={handleBack}
              className="rounded-[8px] px-[12px] py-[7px] text-[13px] font-semibold text-text-secondary hover:bg-background-quarternary"
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={handleNext}
            data-testid="audiolab-tour-next"
            className="rounded-[8px] bg-brand-primary px-[14px] py-[7px] text-[13px] font-semibold text-white hover:opacity-90"
          >
            {isLast ? "Finish" : "Next"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
